import React, { useEffect, useRef, useCallback } from "react";
import styled, { useTheme } from "styled-components";
import { useContextMenu } from "react-contexify";
import InputContextMenu from "./InputContextMenu";

import { ReactComponent as MediaIcon } from "../../Assets/icons/context-menu-icons/MediaIcon.svg";
import { ReactComponent as FileIcon } from "../../Assets/icons/context-menu-icons/FilesIcon.svg";
import { ReactComponent as SendAttachments } from "../../Assets/iconsv2/chat/MediaButton.svg";
import { ReactComponent as SendAttachmentsLight } from "../../Assets/iconsv2/chat/MediaButtonLight.svg";
import { ReactComponent as SendMessageIcon } from "../../Assets/iconsv2/chat/SendButton.svg";
import { Popover } from "react-tiny-popover";
import AttachmentMenu from "./AttachmentMenu";
import { useTranslate } from '@tolgee/react';
import ClosedQuickChatInfo from "../QuickChat/ClosedQuickChatInfo";
import MessageMood from "./MessageMood";
import TextareaAutosize from "react-textarea-autosize";
import { useState } from "react";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useNotify } from "../../Helpers/Notifications";

const ChatInputComponent = ({
  selectedConversation,
  handleSendButton,
  mediaInputFileRef,
  fileInputRef,
  messageInputRef,
  setMediaInput,
  setFileInput,
  isAttachmentMenuOpen,
  setIsAttachmentMenuOpen,
  inputValue,
  handleInputChange,
  showEditMessageModal,
  showReplyMessageModal,
  handleCloseReply,
  handleCloseEdit,
  togleModselector,
  messageMood,
}) => {

  const [isSending, setIsSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { theme } = useGenericComponentsStateService()
  const { notifySuccess, notifyError, notifyInfo } = useNotify();
  const { t } = useTranslate();

  const INPUT_MENU_ID = "input-context-menu";
  const { show } = useContextMenu({ id: INPUT_MENU_ID });
  const textareaRef = useRef(null);

const handleInputContextMenu = (e) => {
  e.preventDefault();
  const textarea = messageInputRef.current;
  if (textarea) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    textarea.dispatchEvent(new CustomEvent("update-selection", {
      detail: { selection: { start, end } }
    }));
  }
  
  show({ 
    event: e,
    position: { x: e.clientX, y: e.clientY - 130 }
  });
};

  const attachmentOptionsList = [
    {
      text: t("general.media"),
      icon: <MediaIcon />,
      action: () => handleOpenUploadMedia(),
    },
    {
      text: t("general.files"),
      icon: <FileIcon />,
      action: () => handleOpenUploadFile(),
    },
  ];

  const handleEsc = (e) => {
    if (showEditMessageModal) {
      if (e.key === "Escape") {
        handleCloseEdit();
      }
    }

    if (showReplyMessageModal) {
      if (e.key === "Escape") {
        handleCloseReply();
      }
    }
  };

  const sendText = async () => {
    if (inputValue.trim().length === 0 || isSending) return;
    setIsSending(true);
    try {
      await handleSendButton();
    } catch (err) {
      console.error("Send failed", err);
    } finally {
      // Add a slight delay if needed
      setTimeout(() => setIsSending(false), 300);
    }
  }

  const handleKeyDown = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText();
    };


  }

  const handleOpenUploadMedia = () => {
    mediaInputFileRef.current.click();
  };

  const handleOpenUploadFile = () => {
    fileInputRef.current.click();
  };

  const handleMediaInputFileChange = (e) => {
    if (!e.target.files) {
      return;
    }

    const inputFile = e.target.files[0];
    const fileType = inputFile.type.split("/");
    if (fileType[0] == "image" || fileType[0] == "video") {
      setMediaInput(inputFile);
    } else {
      notifyError("Please select valid file");
      return;
    }

  };

  const handleInputFileChange = (e) => {
    if (!e.target.files) {
      return;
    }
    const inputFile = e.target.files[0];
    setFileInput(inputFile);
  };

  const handleAutoExpandInputChange = (e) => {
    handleInputChange(e);
    setExpanded(e.target.scrollHeight > 42);
  };


  useEffect(() => {
    const handlePaste = (event) => {
      if (!event.clipboardData) return;

      const files = event.clipboardData.files;

      if (files.length > 0) {
        event.preventDefault(); // Prevent pasting text

        const pastedFile = files[0];
        const fileType = pastedFile.type.split("/")[0];

        if (fileType === "image" || fileType === "video") {
          setMediaInput(pastedFile);
          notifySuccess(t('chat.media_file_pasted'));
        } else {
          setFileInput(pastedFile);
          notifySuccess(t('chat.file_pasted'));
        }
      }
    };

    if (messageInputRef.current) {
      messageInputRef.current.addEventListener("paste", handlePaste);
    }

    return () => {
      if (messageInputRef.current) {
        messageInputRef.current.removeEventListener("paste", handlePaste);
      }
    };
  }, [messageInputRef, setMediaInput, setFileInput, notifySuccess]);

  const setTextareaRef = useCallback((element) => {
    textareaRef.current = element;
    if (messageInputRef) {
      if (typeof messageInputRef === 'function') {
        messageInputRef(element);
      } else {
        messageInputRef.current = element;
      }
    }
    if (element) {
      setTimeout(() => {
        element.focus();
        const length = element.value.length;
        element.setSelectionRange(length, length);
      }, 0);
    }
  }, [messageInputRef]);


  return (
    <div>
      {selectedConversation?.quick_chat &&
        selectedConversation?.open === false ? (
        <>
          <ClosedQuickChatInfo />
        </>
      ) : (
        <>
          {!selectedConversation?.with_emotii && (
            <>
            <MessageInputWrapper onKeyDown={(e) => handleKeyDown(e)}>
              <input
                type="file"
                hidden
                ref={mediaInputFileRef}
                onChange={(e) => handleMediaInputFileChange(e)}
                accept="image/*, video/* "
              />
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={(e) => handleInputFileChange(e)}
                accept="application/*"
              />

              <Popover
                isOpen={isAttachmentMenuOpen}
                onClickOutside={() => setIsAttachmentMenuOpen(false)}
                positions={["top"]}
                padding={10}
                transform={{ left: 70 }}
                transformMode="relative"
                content={<AttachmentMenu optionsList={attachmentOptionsList} />}
              >
                <AttachmentButton
                  onClick={(e) => setIsAttachmentMenuOpen(true)}
                >
                  {theme === "light" ? <SendAttachmentsLight /> : <SendAttachments />}
                </AttachmentButton>
              </Popover>

              <StyledTextareaAutosize
                onKeyDown={(e) => handleEsc(e)}
                onContextMenu={handleInputContextMenu}
                key={selectedConversation?.id}
                ref={setTextareaRef}
                placeholder={t('chat.type_your_message')}
                value={inputValue}
                onChange={(e) => handleAutoExpandInputChange(e)}
                maxRows={4}
                minRows={1}
                expanded={expanded}
              />

              <RightEndWrapper>
                <MessageMood onClick={togleModselector} mood={messageMood} />
                <SendMessageButton
                  disabled={inputValue.length == 0}
                  onClick={() => sendText()}
                >
                  <SendMessageIcon />
                </SendMessageButton>
              </RightEndWrapper>
            </MessageInputWrapper>
            <InputContextMenu 
              inputRef={messageInputRef}
              onCopy={() => {}}
              onCut={handleInputChange}
              onPaste={handleInputChange}
            />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ChatInputComponent;

const MessageInputWrapper = styled.div`
  display: flex;
  background: ${({ theme }) => theme.chat.messageInputWrapperBackground};
  border-top:1px solid ${({ theme }) => theme.sidebar.borderColor};
  padding: 16px;
  height: 74px;
  min-height: 74px;
  height: 100%;
  gap: 12px;
  align-items: end;
`;
const AttachmentButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 100%;
  display: flex;
  jusitfy-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: none;
    


`;
const SendMessageButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 100%;
  display: flex;
  jusitfy-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: none;

  svg{
    fill:${({ theme }) => theme.chat.sendBtn};
     color:${({ theme }) => theme.chat.sendBtnBorder};
    }

  &:disabled {
    opacity: 0.5;
    cursor: inherit;
  }
`;
const RightEndWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledTextareaAutosize = styled(TextareaAutosize)`
width:100%;
padding:10px 16px;
border:none;
background:${({ theme }) => theme.chat.messageInputBackground};
resize:none;
color:${({ theme }) => theme.chat.messageInputTextColor};
font-size:16px;
font-weight:400;
line-height:22px;
text-align:left;
word-break:break-word;
transition:border-radius 0.2s ease-in-out, height 0.2s ease-in-out;
border-radius:${({ expanded }) => expanded ? "4px" : "4px"};
outline:none;
`